// @flow
import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import makeStyles from '@material-ui/core/styles/makeStyles';
import {colors} from '../../assets/styles/colors';
import {
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Typography,
  useMediaQuery,
  IconButton,
} from '@material-ui/core';
import IosSwitch from '../IosSwitch/IosSwitch';
import CustomButton from '../CustomButton/CustomButton';
import {commaFormat} from '../../utils/number';
import {getImageUrl} from '../../utils/image_provider';
import {
  AddCircleTwoTone,
  Edit,
  StarRounded,
  Warning,
  FileCopy,
} from '@material-ui/icons';
import {getProductItemPricing} from '../../utils/product';

import {useIsFeatureInTier} from '../../utils/component';
import type {Currency, Product} from '../../entities';
import TiendadaTooltip from '../tiendada-tooltip';

type ProductCardType = 'CREATE' | 'EDIT';

type Props = {
  product?: Product,
  handleProductEnable?: (id: string, enable: boolean) => void,
  handleProductFeature?: (id: string, featured: boolean) => void,
  handleStockModalOpen?: (id: string) => void,
  handleClick?: (p?: Product) => void,
  type?: ProductCardType,
  text?: string,
  currency?: Currency,
  onDuplicate?: (product: Product) => void,
};
const useStyles = props =>
  makeStyles(theme => ({
    cardRoot: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      position: 'relative',
      // marginBottom: theme.spacing(1),
      // maxWidth: 248,
      height: '100%',
      maxWidth: 282,
      width: '100%',
      boxShadow: 'unset',
      margin: '0 auto',
      [theme.breakpoints.down('sm')]: {
        // maxWidth: 200,
        // maxWidth: 150,
      },
    },
    cardActionArea: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      height: '100%',
      borderRadius: '12px',
    },
    cardMedia: {
      borderRadius: '12px',
      height: 248,
      width: '100%',
      filter:
        props.product && !props.product.enabled ? 'grayscale(100%)' : null,
      transition: 'opacity 0.2s, filter 0.2s',
      [theme.breakpoints.down('xs')]: {
        maxHeight: 200,
      },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    },
    disabledAlert: {
      margin: theme.spacing(1, 2),
      padding: 10,
      textAlign: 'center',
      backgroundColor: 'rgba(255, 255, 255, 0.7);',
      borderRadius: '12px',
      // opacity:
      //   props.product &&
      //   props.product.enabled &&
      //   !(
      //     props.product.stockSetting &&
      //     props.product.stockSetting.enabled &&
      //     props.product.stockSetting.stock === 0
      //   )
      //     ? 0
      //     : 1,
      transition: 'opacity 0.2s',
    },
    disabledText: {
      fontSize: '0.875rem',
      fontWeight: 500,
      [theme.breakpoints.down('xs')]: {
        fontSize: '0.75rem',
      },
    },
    cardContent: {
      padding: 0,
    },
    productTitle: {
      lineHeight: theme.spacing(3),
      margin: theme.spacing(2, 0, 1, 0),
      opacity: props.product && !props.product.enabled ? 0.5 : 'initial',
      transition: 'opacity 0.2s',
      // height: theme.spacing(6),
      // whiteSpace: 'nowrap',
      overflow: 'hidden',
      display: '-webkit-box',
      '-webkit-line-clamp': 2,
      '-webkit-box-orient': 'vertical',
      'text-overflow': 'ellipsis',
    },
    productPrice: {
      lineHeight: theme.spacing(3),
      opacity: props.product && !props.product.enabled ? 0.5 : 'initial',
      transition: 'opacity 0.3s',
      fontWeight: 600,
      fontSize: '1.2rem',
      overflow: 'hidden',
      display: '-webkit-box',
      '-webkit-line-clamp': 2,
      '-webkit-box-orient': 'vertical',
      'text-overflow': 'ellipsis',
    },
    productPriceLabel: {
      fontWeight: 600,
      fontSize: '1rem',
      overflow: 'hidden',
      display: '-webkit-box',
      '-webkit-line-clamp': 3,
      '-webkit-box-orient': 'vertical',
      'text-overflow': 'ellipsis',
    },
    productRealPrice: {
      color: '#B8B8C7',
      fontWeight: 500,
      fontSize: '14px',
      textDecoration: 'line-through',
      marginLeft: '10px',
      [theme.breakpoints.down('xs')]: {
        marginLeft: 0,
      },
    },
    containerPrice: {
      display: 'flex',
      alignItems: 'center',
      [theme.breakpoints.down('xs')]: {
        alignItems: 'flex-start',
        flexDirection: 'column-reverse',
      },
    },
    empty: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: 320,
      height: '100%',
      width: '100%',
      border: '2px dashed #B8B8C7',
      opacity: 0.6,
      // boxSizing: 'borderBox',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      padding: 20,
      borderRadius: '12px',
      '&:hover': {
        opacity: 1,
      },
      transition: 'opacity 0.2s',
      [theme.breakpoints.down('xs')]: {
        // maxWidth: '100%',
        minHeight: 272,
      },
    },
    agregarText: {
      fontSize: 14,
      margin: theme.spacing(1, 0),
    },
    switchContainer: {
      position: 'absolute',
      top: 4,
      right: 4,
    },
    duplicateButton: {
      position: 'absolute',
      top: 4,
      right: 64,
      backdropFilter: 'blur(.5rem)',
      backgroundColor: 'rgba(38, 38, 38, 0.18)',
      boxShadow: '0px 0px 1rem rgba(96, 97, 112, 0.16)',
      color: 'white',
    },
    variantsBadge: {
      position: 'absolute',
      bottom: 82,
      left: 10,
      padding: '4px 12px',
      borderRadius: '24px',
      backgroundColor: colors.grey200,
      boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.12)',
      fontWeight: 600,
    },
    featuredToggle: {
      position: 'absolute',
      right: 4,
      bottom: 4,
      boxShadow: '0px 0px 2px 2px rgba(96, 97, 112, 0.16)',
    },
    stockButton: {
      position: 'absolute',
      bottom: 82,
      right: 10,
      padding: '2px 10px 2px 12px',
      borderRadius: '24px',
      backgroundColor: colors.grey200,
      boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.12)',
      fontWeight: 600,
    },
    verticalDivider: {
      height: 16,
      width: 1,
      margin: '0px 4px',
      backgroundColor: colors.grey500,
    },
    warningAlert: {
      position: 'absolute',
      left: 12,
      top: 12,
      background: colors.warning,
      padding: 6,
      fontSize: '2rem',
      '&:hover': {
        color: colors.warning,
      },
    },
  }));

export default function ProductCard(props: Props) {
  const {
    product,
    currency,
    handleProductEnable,
    handleProductFeature,
    handleStockModalOpen,
    handleClick,
    onDuplicate,
    type,
    text,
  } = props;

  // const hasTaxesFeature = useIsFeatureInTier('TAXES');

  // const isFromExternalResource = !!product?.metadataSetting?.source;
  const isFromExternalResource = false;

  const classes = useStyles(props)();
  const mobile = useMediaQuery('(max-width:600px)');

  const productTaxFactor = 1;

  const isEnabledProductVariants =
    !!product?.productVariantSetting.enabled &&
    !!product?.productVariantSetting.variants &&
    !!product.productVariantSetting.variants.length;

  const priceLabel = product?.metadataSetting?.priceDisplay?.label;
  const priceLabelDisplayMode =
    product?.metadataSetting?.priceDisplay?.mode ?? 'PRICE';

  const outOfStock = useMemo(() => {
    if (product && product.stockSetting && product.stockSetting.enabled) {
      if (
        product.productVariantSetting?.enabled &&
        product.productVariantSetting.variants &&
        product.productVariantSetting.variants.length
      ) {
        const variants = product.productVariantSetting.variants;
        for (let i = 0; i < variants.length; i++) {
          if (variants[i].stock && variants[i].stock > 0) return false;
        }
        return true;
      }
      if (product.stockSetting.stock === 0) return true;
    }
    return false;
  }, [product]);

  const userHasProductCreationFeature = useIsFeatureInTier('PRODUCT_CREATION');
  const userHasProductEditFeature = useIsFeatureInTier('PRODUCT_EDIT');
  const userHasProductDisableFeature = useIsFeatureInTier('PRODUCT_DISABLE');
  const userHasFeaturedProductsFeature = useIsFeatureInTier(
    'FEATURED_PRODUCTS'
  );

  const productPricing = useMemo(
    () => product && getProductItemPricing(product),
    [product]
  );

  const variantOptionsMissing = useMemo(() => {
    if (!product) return false;
    const variantSetting = product?.productVariantSetting;
    if (variantSetting) {
      return (
        variantSetting.enabled &&
        !variantSetting.variants &&
        !variantSetting.options
      );
    }
    return false;
  }, [product]);

  const buildContent = () => {
    if (type === 'CREATE') {
      return (
        <Card classes={{root: classes.cardRoot}}>
          {handleClick && (
            <CardActionArea
              className={classes.cardActionArea}
              style={
                !userHasProductCreationFeature
                  ? {pointerEvents: 'none'}
                  : undefined
              }
              onClick={
                !userHasProductCreationFeature ? undefined : () => handleClick()
              }
            >
              {text && (
                <div className={classes.empty}>
                  <AddCircleTwoTone color="primary" fontSize="large" />
                  <Typography className={classes.agregarText}>
                    {text}
                  </Typography>
                </div>
              )}
            </CardActionArea>
          )}
        </Card>
      );
    } else if (type === 'EDIT') {
      return product ? (
        <Card className={classes.cardRoot}>
          {handleClick && (
            <CardActionArea
              className={classes.cardActionArea}
              style={
                !userHasProductEditFeature ? {pointerEvents: 'none'} : undefined
              }
              onClick={
                !userHasProductEditFeature
                  ? undefined
                  : () => handleClick(product)
              }
            >
              {variantOptionsMissing ? (
                <CardMedia
                  classes={{root: classes.cardMedia}}
                  style={{
                    opacity: !product.enabled || outOfStock ? 0.75 : 'initial',
                  }}
                  image={getImageUrl(product.mainImageId)}
                  title={product.name}
                >
                  <div className={classes.disabledAlert}>
                    <Typography className={classes.disabledText}>
                      El producto se ocultará en la tienda virtual por no tener
                      variantes configuradas
                    </Typography>
                  </div>
                </CardMedia>
              ) : (
                <CardMedia
                  classes={{root: classes.cardMedia}}
                  style={{
                    opacity: !product.enabled || outOfStock ? 0.75 : 'initial',
                  }}
                  image={getImageUrl(product.mainImageId)}
                  title={product.name}
                >
                  {(!product.enabled || outOfStock) && (
                    <div className={classes.disabledAlert}>
                      <Typography className={classes.disabledText}>
                        {!product.enabled
                          ? 'Este producto no aparecerá en su tienda virtual'
                          : 'Sin stock'}
                      </Typography>
                    </div>
                  )}
                </CardMedia>
              )}
              <CardContent className={classes.cardContent}>
                <Typography align="left" className={classes.productTitle}>
                  {product.name}
                </Typography>

                {priceLabelDisplayMode === 'PRICE' ? (
                  <div className={classes.containerPrice}>
                    {productPricing && productPricing.offerPrice ? (
                      <>
                        <Typography
                          className={classes.productPrice}
                          align="left"
                        >
                          {commaFormat(
                            parseFloat(productPricing.offerPrice) *
                              productTaxFactor,
                            currency ? currency.symbol : ''
                          )}
                        </Typography>
                        <Typography
                          className={classes.productRealPrice}
                          align="right"
                        >
                          {commaFormat(
                            parseFloat(productPricing.price) * productTaxFactor,
                            currency ? currency.symbol : ''
                          )}
                        </Typography>
                      </>
                    ) : (
                      <Typography className={classes.productPrice} align="left">
                        {commaFormat(
                          productPricing?.price
                            ? parseFloat(productPricing?.price) *
                                productTaxFactor
                            : '',
                          currency ? currency.symbol : ''
                        )}
                      </Typography>
                    )}
                  </div>
                ) : (
                  <div className={classes.containerPrice}>
                    <Typography
                      className={classes.productPriceLabel}
                      align="left"
                    >
                      {priceLabel}
                    </Typography>
                  </div>
                )}
              </CardContent>
            </CardActionArea>
          )}
          {handleProductEnable &&
            !isFromExternalResource &&
            userHasProductDisableFeature && (
              <IosSwitch
                className={classes.switchContainer}
                checked={product.enabled}
                onChange={() =>
                  handleProductEnable(product.id, product.enabled)
                }
                name="enabled"
              />
            )}

          <TiendadaTooltip title="Duplicar producto" placement="top">
            <IconButton
              className={classes.duplicateButton}
              size="medium"
              disabled={!userHasProductEditFeature}
              onClick={() => {
                if (onDuplicate) {
                  onDuplicate(product);
                }
              }}
            >
              <FileCopy />
            </IconButton>
          </TiendadaTooltip>
          {variantOptionsMissing && (
            <TiendadaTooltip
              title="Actualicé las variantes: no hay opciones disponibles."
              placement="top"
            >
              <IconButton
                color="default"
                className={classes.warningAlert}
                size="medium"
              >
                <Warning />
              </IconButton>
            </TiendadaTooltip>
          )}
          {isEnabledProductVariants &&
            product.productVariantSetting &&
            product.productVariantSetting.variants &&
            product.productVariantSetting.variants.length > 1 && (
              <div className={classes.variantsBadge}>
                {`${product.productVariantSetting.variants.length} ${
                  !mobile ? 'Variantes' : ''
                }`}
              </div>
            )}
          {userHasProductEditFeature &&
            !isFromExternalResource &&
            handleStockModalOpen &&
            product.stockSetting &&
            product.stockSetting.enabled && (
              <CustomButton
                variant="contained"
                color="default"
                className={classes.stockButton}
                onClick={() => handleStockModalOpen(product.id)}
              >
                {isEnabledProductVariants
                  ? 'Stock'
                  : product.stockSetting.stock}
                <div className={classes.verticalDivider} />
                <Edit fontSize="small" />
              </CustomButton>
            )}
          {userHasFeaturedProductsFeature &&
            handleProductFeature &&
            product.enabled && (
              <IconButton
                className={classes.featuredToggle}
                onClick={() =>
                  handleProductFeature(product.id, !!product.featured)
                }
                size="small"
              >
                <StarRounded
                  style={{
                    color: product.featured ? '#FFC107' : colors.grey500,
                  }}
                />
              </IconButton>
            )}
        </Card>
      ) : null;
    } else {
      return <div className={classes.empty} />;
    }
  };
  return buildContent();
}

ProductCard.propTypes = {
  product: PropTypes.object,
  handleProductEnable: PropTypes.func,
  handleProductFeature: PropTypes.func,
  handleStockModalOpen: PropTypes.func,
  handleClick: PropTypes.func,
  type: PropTypes.string,
  text: PropTypes.string,
  currency: PropTypes.object,
};
